// File: /src/components/finance/FinanceGenReports.js

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';

// Função auxiliar para formatar valores em Real
const formatCurrency = (value) =>
	Number(value).toLocaleString('pt-BR', {
		style: 'currency',
		currency: 'BRL',
	});

// Função auxiliar para formatar datas como DD/MM/AAAA
const formatDate = (date) => {
	if (!date) return '-';
	const d = new Date(date);
	if (isNaN(d)) return '-';
	return d.toLocaleDateString('pt-BR');
};

/* Relatório Agregado – Resumo Financeiro */
export const generateFinanceSummaryReport = (
	summary,
	filters = {},
	fileType = 'pdf'
) => {
	if (!summary) {
		toast.error('Erro ao carregar o resumo financeiro.');
		return;
	}
	const data = [
		['Receitas', formatCurrency(summary.totalIncome)],
		['Despesas', formatCurrency(summary.totalExpense)],
		['Faturas Pagas', formatCurrency(summary.totalReceived)],
		['Faturas Pendentes', formatCurrency(summary.totalPending)],
		['Faturas Atrasadas', formatCurrency(summary.totalOverdue)],
		['Resultado Líquido', formatCurrency(summary.netTotal)],
	];

	if (fileType === 'pdf') {
		const doc = new jsPDF();
		doc.setFontSize(12);
		doc.text('Resumo Financeiro', 14, 15);
		autoTable(doc, {
			head: [['Categoria', 'Total']],
			body: data,
			startY: 20,
			theme: 'grid',
			styles: { fontSize: 6, cellPadding: 3 },
			headStyles: {
				fillColor: [41, 128, 185],
				textColor: [255, 255, 255],
			},
		});
		doc.save('resumo_financeiro.pdf');
		toast.success('Relatório de resumo financeiro gerado com sucesso!');
	} else if (fileType === 'xlsx') {
		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet([
			['Categoria', 'Total'],
			...data,
		]);
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Resumo Financeiro');
		const buffer = XLSX.write(workbook, {
			bookType: 'xlsx',
			type: 'array',
		});
		saveAs(
			new Blob([buffer], { type: 'application/octet-stream' }),
			'resumo_financeiro.xlsx'
		);
		toast.success('Relatório de resumo financeiro gerado com sucesso!');
	}
};

/* Relatório Detalhado de Faturas */
export const generateInvoiceReport = (
	invoices = [],
	projects = [], // Alterado de services para projects
	clients = [],
	filters = {},
	fileType = 'pdf'
) => {
	if (!Array.isArray(invoices) || !Array.isArray(projects) || !Array.isArray(clients)) {
		console.error('Arrays inválidos:', { invoices, projects, clients });
		toast.error('Erro ao carregar os dados das faturas.');
		return;
	}

	// Criar Maps para busca eficiente
	const projectsMap = new Map(projects.map(project => [project.id, project]));
	const clientsMap = new Map(clients.map(client => [client.id, client]));

	// Aplica filtros, se houver
	const filteredInvoices = invoices.filter((inv) => {
		if (filters.status && inv.status !== filters.status) return false;
		if (filters.minValue && inv.value < filters.minValue) return false;
		if (filters.maxValue && inv.value > filters.maxValue) return false;
		if (filters.startDate && new Date(inv.due_date) < new Date(filters.startDate)) return false;
		if (filters.endDate && new Date(inv.due_date) > new Date(filters.endDate)) return false;
		return true;
	});

	// Cria um array detalhado com dados do cliente e do projeto
	const detailedInvoices = filteredInvoices.map((inv) => {
		// Encontra o projeto usando projectId
		const project = projectsMap.get(inv.projectId);
		
		// Encontra o cliente relacionado ao projeto
		const client = project ? clientsMap.get(project.clientId) : null;

		return {
			id: inv.id || '',
			clientName: client?.name || 'Cliente não vinculado',
			projectName: project?.name || 'Projeto não vinculado',
			description: inv.description || 'Sem descrição',
			value: formatCurrency(inv.value || 0),
			dueDate: formatDate(inv.due_date),
			status: inv.status === 'paid' ? 'Paga' : 
					inv.status === 'pending' ? 'Pendente' : 'Atrasada',
		};
	});

	const data = detailedInvoices.map((d) => [
		d.id,
		d.clientName,
		d.projectName,
		d.description,
		d.value,
		d.dueDate,
		d.status,
	]);

	if (fileType === 'pdf') {
		const doc = new jsPDF();
		doc.setFontSize(12);
		doc.text('Relatório de Faturas Detalhado', 14, 15);
		autoTable(doc, {
			head: [
				[
					'ID',
					'Cliente',
						'Projeto',
					'Descrição',
					'Valor',
					'Data de Vencimento',
					'Status',
				],
			],
			body: data,
			startY: 20,
			theme: 'grid',
			styles: { fontSize: 6, cellPadding: 3 },
			headStyles: {
				fillColor: [41, 128, 185],
				textColor: [255, 255, 255],
			},
		});
		doc.save('relatorio_faturas.pdf');
		toast.success('Relatório de faturas gerado com sucesso!');
	} else if (fileType === 'xlsx') {
		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet([
			[
				'ID',
				'Cliente',
					'Projeto',
				'Descrição',
				'Valor',
				'Data de Vencimento',
				'Status',
			],
			...data,
		]);
		XLSX.utils.book_append_sheet(
			workbook,
			worksheet,
			'Relatório de Faturas'
		);
		const buffer = XLSX.write(workbook, {
			bookType: 'xlsx',
			type: 'array',
		});
		saveAs(
			new Blob([buffer], { type: 'application/octet-stream' }),
			'relatorio_faturas.xlsx'
		);
		toast.success('Relatório de faturas gerado com sucesso!');
	}
};

/* Relatório de Comparação Temporal */
export const generateTemporalComparisonReport = (
	lineData,
	period,
	filters = {},
	fileType = 'pdf'
) => {
	if (!Array.isArray(lineData)) {
		toast.error('Erro ao carregar dados de comparação temporal.');
		return;
	}

	// Mapeia e filtra os dados
	const filteredLineData = lineData
		.map((entry) => {
			if (filters.type === 'income') {
				return { month: entry.month, income: entry.income, expense: 0 };
			} else if (filters.type === 'expense') {
				return { month: entry.month, income: 0, expense: entry.expense };
			}
			return entry;
		})
		// Remove entradas onde tanto receita quanto despesa são zero
		.filter(entry => {
			const income = Number(entry.income) || 0;
			const expense = Number(entry.expense) || 0;
			return income > 0 || expense > 0;
		});

	// Se não houver dados após a filtragem
	if (filteredLineData.length === 0) {
		toast.warn('Não há movimentações financeiras no período selecionado.');
		return;
	}

	const data = filteredLineData.map((entry) => [
		formatDate(entry.month),
		formatCurrency(entry.income || 0),
		formatCurrency(entry.expense || 0),
	]);

	if (fileType === 'pdf') {
		const doc = new jsPDF();
		doc.setFontSize(12);
		doc.text(
			`Comparação Temporal Receitas x Despesas (${
				period === 'monthly' ? 'Mensal' : 'Anual'
			})`,
			14,
			15
		);
		autoTable(doc, {
			head: [['Período', 'Receitas', 'Despesas']],
			body: data,
			startY: 20,
			theme: 'grid',
			styles: { fontSize: 6, cellPadding: 3 },
			headStyles: {
				fillColor: [41, 128, 185],
				textColor: [255, 255, 255],
			},
		});
		doc.save(`comparacao_temporal_${period}.pdf`);
		toast.success('Relatório de comparação temporal gerado com sucesso!');
	} else if (fileType === 'xlsx') {
		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet([
			['Período', 'Receitas', 'Despesas'],
			...data,
		]);
		XLSX.utils.book_append_sheet(
			workbook,
			worksheet,
			'Comparação Temporal RxD'
		);
		const buffer = XLSX.write(workbook, {
			bookType: 'xlsx',
			type: 'array',
		});
		saveAs(
			new Blob([buffer], { type: 'application/octet-stream' }),
			`comparacao_temporal_${period}.xlsx`
		);
		toast.success('Relatório de comparação temporal gerado com sucesso!');
	}
};

/* Relatório Personalizado */
export const generateCustomReport = (
	entries,
	filters = {},
	fileType = 'pdf'
) => {
	if (!Array.isArray(entries)) {
		toast.error(
			'Erro ao carregar os dados para o relatório personalizado.'
		);
		return;
	}
	let filteredEntries = entries.filter((entry) => {
		if (filters.minValue && entry.value < filters.minValue) return false;
		if (filters.maxValue && entry.value > filters.maxValue) return false;
		if (
			filters.startDate &&
			new Date(entry.date) < new Date(filters.startDate)
		)
			return false;
		if (filters.endDate && new Date(entry.date) > new Date(filters.endDate))
			return false;
		return true;
	});
	if (filters.type && filters.type !== 'all') {
		filteredEntries = filteredEntries.filter(
			(entry) => entry.type === filters.type
		);
	}
	if (filters.descriptionKeyword) {
		filteredEntries = filteredEntries.filter((entry) =>
			entry.description
				.toLowerCase()
				.includes(filters.descriptionKeyword.toLowerCase())
		);
	}
	const data = filteredEntries.map((entry) => [
		entry.description,
		formatCurrency(entry.value),
		formatDate(entry.date),
		entry.type === 'income' || entry.type === 'invoice'
			? 'Receita'
			: 'Despesa',
	]);

	if (fileType === 'pdf') {
		const doc = new jsPDF();
		doc.setFontSize(12);
		doc.text('Relatório Personalizado', 14, 15);
		autoTable(doc, {
			head: [['Descrição', 'Valor', 'Data', 'Tipo']],
			body: data,
			startY: 20,
			theme: 'grid',
			styles: { fontSize: 6, cellPadding: 3 },
			headStyles: {
				fillColor: [41, 128, 185],
				textColor: [255, 255, 255],
			},
		});
		doc.save('relatorio_personalizado.pdf');
		toast.success('Relatório personalizado gerado com sucesso!');
	} else if (fileType === 'xlsx') {
		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet([
			['Descrição', 'Valor', 'Data', 'Tipo'],
			...data,
		]);
		XLSX.utils.book_append_sheet(
			workbook,
			worksheet,
			'Relatório Personalizado'
		);
		const buffer = XLSX.write(workbook, {
			bookType: 'xlsx',
			type: 'array',
		});
		saveAs(
			new Blob([buffer], { type: 'application/octet-stream' }),
			'relatorio_personalizado.xlsx'
		);
		toast.success('Relatório personalizado gerado com sucesso!');
	}
};
